import anylogger from 'anylogger';
import { initAll, track, identify, setUserId, setGroup } from '@amplitude/unified';
import { Identify } from '@amplitude/analytics-browser';
import type { ApiUserObject } from '@/api/me';
import { useUserStore } from '@/stores/userStore';
import { parseHeadMetaTag } from '@/util/headMetaTags';
import { isTestEnvironment } from '@/util/env';
import {
    buildAnalyticsIdentity,
    pickEventUserProperties,
    type AnalyticsIdentity,
} from '@/domain/analytics/analyticsIdentity';

const logger = anylogger('Amplitude');

const DEV_API_KEY = '4ac249f924e92a695bbfcf4e0e208f83';

export async function configureAmplitude() {
    const api_key = parseHeadMetaTag('amplitude-api-key', DEV_API_KEY);
    await initAll(api_key, {
        engagement: {},
        sessionReplay: {
            // Session Replay configuration options
            sampleRate: 1, // To enable session replay
            // Privacy configuration to prevent PII capture
            privacyConfig: {
                defaultMaskLevel: 'conservative',
            },
        },
    });

    const guidesAndSurveys = window.engagement.gs.getAllGuidesAndSurveys();

    guidesAndSurveys.forEach((guideOrSurvey) => {
        if (guideOrSurvey.type === 'survey') {
            //reset any existing survey state to ensure it can be shown again
            window.engagement.gs.reset(guideOrSurvey.flagKey);
        }
    });

    // ensures that dismissed guides/surveys are reset so they can be shown again
    window.engagement.addIntegration({
        track: (event) => {
            // Check if the event type includes 'Dismissed' to catch all dismissals
            if (event.event_type.includes('Dismissed') && event.event_properties) {
                const nudgeKey = (event.event_properties as Record<string, string>)[
                    '[Guides-Surveys] Key'
                ];
                window.setTimeout(() => window.engagement.gs.reset(nudgeKey), 1000);
            }
        },
    });

    // load error tracking after amplitude is configured
    setupGlobalErrorTracking();
}

/**
 * Sets up global error handlers to capture all unhandled errors and rejections.
 * Call this after configureAmplitude() to ensure error tracking is initialized.
 */
function setupGlobalErrorTracking(): void {
    // Capture unhandled errors
    window.addEventListener('error', (event: ErrorEvent) => {
        trackError(event.error || new Error(event.message), {
            error_type: 'unhandled_error',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const error =
            event.reason instanceof Error ? event.reason : new Error(String(event.reason));
        trackError(error, {
            error_type: 'unhandled_rejection',
        });
    });
}

export async function identifyUser(user: ApiUserObject): Promise<void> {
    if (isTestEnvironment()) {
        logger.warn('Amplitude.identify() not called in test environment');
        return;
    }

    const identity = buildAnalyticsIdentity(user, new Date());
    // The Amplitude id, never the email: an email is a direct personal identifier and
    // application logs are not a lawful place to hold one.
    logger.debug(`Identifying user: ${identity.userId}`);

    setUserId(identity.userId);
    identify(toIdentifyEvent(identity));
    applyGroupIdentity(identity);
}

function toIdentifyEvent(identity: AnalyticsIdentity): Identify {
    const identifyEvent = new Identify();
    Object.entries(identity.userProperties).forEach(([key, value]) => {
        identifyEvent.set(key, value);
    });
    return identifyEvent;
}

/**
 * Group identity is optional: it needs a vendor add-on that may not be live.
 * A failure here must not cost the user properties that were already sent, so
 * it is contained rather than propagated. The call is deliberately not awaited
 * — its thenable result would tie identification to that add-on.
 */
function applyGroupIdentity(identity: AnalyticsIdentity): void {
    try {
        identity.groups.forEach((group) => setGroup(group.groupType, group.groupName));
    } catch (error) {
        logger.warn('Amplitude.setGroup() failed', error);
    }
}

export async function trackEvent(
    eventName: string,
    eventProperties?: Record<string, unknown>,
): Promise<void> {
    const hipUser = useUserStore();
    if (!hipUser.isLoaded) {
        await hipUser.load();
    }

    const identity = buildAnalyticsIdentity(hipUser.user, new Date());

    track({
        event_type: eventName,
        user_id: identity.userId,
        user_properties: pickEventUserProperties(identity),
        event_properties: eventProperties,
    });
}

export function trackError(error: Error, customProperties?: Record<string, unknown>): void {
    const errorProperties = {
        error_message: error.message,
        error_name: error.name,
        error_stack: error.stack,
        ...customProperties,
    };

    track({
        event_type: 'error_occurred',
        event_properties: errorProperties,
    });
}
